import { useState, useRef, useEffect } from 'react';
import { AudioMonitor } from '@/utils/audioMonitor';
import { useToast } from '@/hooks/use-toast';

interface MonitorStreams {
  mic?: MediaStream | null;
  system?: MediaStream | null;
}

export const useAudioMonitor = () => {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [monitorVolume, setMonitorVolume] = useState(0.3);
  const monitorRef = useRef<AudioMonitor | null>(null);
  const { toast } = useToast();

  const startMonitoring = (streams: MonitorStreams) => {
    try {
      console.log('[useAudioMonitor] Starting monitoring with streams:', streams);
      
      // Create monitor if it doesn't exist
      if (!monitorRef.current) {
        monitorRef.current = new AudioMonitor();
      }

      // Add mic stream
      if (streams.mic) {
        monitorRef.current.addStream('mic', streams.mic);
      }

      // Add system audio stream
      if (streams.system) {
        monitorRef.current.addStream('system', streams.system);
      }

      setIsMonitoring(true);
      
      toast({
        title: "Audio monitoring started",
        description: "Use headphones to prevent feedback",
      });
    } catch (error) {
      console.error('[useAudioMonitor] Error starting monitoring:', error);
      toast({
        title: "Failed to start monitoring",
        description: "Please check your audio permissions",
        variant: "destructive",
      });
    }
  };

  const stopMonitoring = () => {
    console.log('[useAudioMonitor] Stopping monitoring');
    
    if (monitorRef.current) {
      monitorRef.current.dispose();
      monitorRef.current = null;
    }

    setIsMonitoring(false);
    
    toast({
      title: "Audio monitoring stopped",
    });
  };

  const updateVolume = (volume: number) => {
    setMonitorVolume(volume);
    if (monitorRef.current) {
      monitorRef.current.setVolume(volume);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (monitorRef.current) {
        monitorRef.current.dispose();
        monitorRef.current = null;
      }
    };
  }, []);

  return {
    isMonitoring,
    monitorVolume,
    startMonitoring,
    stopMonitoring,
    setVolume: updateVolume,
  };
};
